import React, { useEffect, useMemo, useState } from "react";
import { ItemTabs } from "./ItemTabs";
import { SectionProps } from "../Sections/SectionProps";
import { ItemState } from "../../redux/state";
import { useForm } from "react-hook-form";
import { authSelector } from "../../redux/selectors";
import {
  buildMetaData,
  getLookupTracker, modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { ItemDetails } from "./ItemDetails";
import { Prompt } from "react-router-dom";
import { ModuleAppBar } from "../ModuleAppBar";
import {
  AppBarButton,
  AppBarSaveButton,
  AppBarValidationIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import { ItemDetailsOfqual } from "./ItemDetailsOfqual";
import { ExpertsApplyModel } from "../../domain/ExpertsApplyFormModel";
import { useDispatch, useSelector } from "react-redux";
import { DialogType, EntityDialog } from "../types";
import { RenderDialog } from "../RenderDialog";
import { updatingItem } from "../../redux/actions";
import { UserRole } from "../../domain/userRole";

type NonNullableItemState = ItemState & {
  item: Exclude<ItemState["item"], null>;
};

export const ItemForm: React.FC<{
  state: NonNullableItemState;
}> = ({
  state: { item, status, lastCrudResult },
}) => {
    const dispatch = useDispatch();
    const [focusedEntity, setFocusedEntity] = useState<EntityDialog | null>(null);
    const {
      control,
      formState,
      errors,
      setValue,
      setError,
      clearErrors,
      getValues,
      reset,
      handleSubmit,
    } = useForm<ExpertsApplyModel>({
      defaultValues: item,
      shouldUnregister: false,
    });

    useEffect(() => {
      // on save (detected by a new item in redux) we need to clear dirtyFields, so reset is required
      reset(item);
    }, [reset, item]);

    const { userType, token, roles } = useSelector(authSelector);

    // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
    const lookupTracker = useMemo(() => getLookupTracker(), []);
    const { dirtyFields } = formState;


    const onSubmit = (newModel: ExpertsApplyModel) => {
      const fieldsToIgnore: (keyof ExpertsApplyModel)[] = ["qualifications"];
      const changedFields = (Object.keys(
        dirtyFields
      ) as (keyof ExpertsApplyModel)[]).filter((x) => !fieldsToIgnore.includes(x));
      const document = modelToJsonPatchDocument(newModel, changedFields);
      const meta = buildMetaData(newModel, item, changedFields, lookupTracker);
      dispatch(updatingItem(item.id, newModel.qualifications, { document, meta }));
    };

    const sectionProps: SectionProps<ExpertsApplyModel> = {
      isCreateMode: false,
      user: { userType, token },
      disabled: false,
      control,
      errors,
      formState,
      item,
      clearErrors,
      setValue: (name, value) => setValue(name, value),
      getValues,
      registerCustomError: (name: string, isError: boolean) => {
        const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
        if (isError && !isCurrentlyARegisteredError) {
          setError(name, {});
        }
        if (!isError && isCurrentlyARegisteredError) {
          clearErrors(name);
        }
      },
      lookupTracker,
    };

    return (
      <>
        <Prompt
          message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
          when={Object.keys(formState.dirtyFields).length > 0}
        />
        <ModuleAppBar breadcrumbs={[{ label: "" }]}>
          <AppBarValidationIndicator errors={errors} />
          <ModuleAppBar breadcrumbs={[{ label: `${item.referenceNumber}` }]}>
            {(roles.includes(UserRole.OfqualExpertsAdmin) || roles.includes(UserRole.ITAdmin)|| roles.includes(UserRole.OfqualExpertsEvaluator)) && (
              <AppBarButton
                text="Change Status"
                tooltip="Change status of current SMS application"
                onClick={() =>
                  setFocusedEntity({
                    dialogType: DialogType.ChangeApplicationStatus,
                  })
                }
                data-test="change-sms-application-status-button"
              />
            )}
              {(roles.includes(UserRole.OfqualExpertsAdmin) || roles.includes(UserRole.ITAdmin) || roles.includes(UserRole.OfqualExpertsEvaluator)) && (
                <AppBarButton
                  text="Add expertise"
                  tooltip="Add a new area of expertise to the SMS application"
                  onClick={() =>
                    setFocusedEntity({
                      dialogType: DialogType.AddExpertiseForm,
                    })
                  }
                  data-test="change-sms-application-status-button"
                />
              )}
            <AppBarSaveButton
              onClick={handleSubmit(onSubmit)}
              isSubmitting={status === "UPDATING"}
              isSubmitted={lastCrudResult.isSuccess}
              dataTest="save"
              disabled={status === "UPDATING"}
            />
            <RenderDialog
              focusedEntity={focusedEntity}
              onClose={() => setFocusedEntity(null)}
              item={item}
              setFocusedEntity={setFocusedEntity}
            />
          </ModuleAppBar>
        </ModuleAppBar>
        <ItemTabs indicatorProps={sectionProps}>
          {(activeTab) =>
            activeTab === "ofqual" ? (
              <ItemDetailsOfqual sectionProps={sectionProps} specialist={item} />
            ) : (
              <ItemDetails sectionProps={sectionProps} state={{
                item,
                status: "NOT_LOADED",
                lastCrudResult: {
                  isSuccess: null
                }
              }} />
            )
          }
        </ItemTabs>
      </>
    );
  };
